import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { getWalletTickets, type WalletTicketSummary } from '../../api/wallet'
import TicketQrModal from './TicketQrModal'

function formatDateTime(iso?: string | null) {
  if (!iso) return 'TBD'
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return 'TBD'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(dt)
}

function statusClasses(status: WalletTicketSummary['status']) {
  if (status === 'cancelled') return 'border-rose-400/40 bg-rose-500/15 text-rose-100'
  if (status === 'redeemed') return 'border-indigo-400/40 bg-indigo-500/15 text-indigo-100'
  if (status === 'past') return 'border-white/20 bg-white/10 text-white/80'
  return 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100'
}

export function receiptLabel(status?: string | null): 'Verified receipt' | 'Pending receipt' | '' {
  if (!status) return ''
  return status === 'anchored' ? 'Verified receipt' : 'Pending receipt'
}

const RECEIPT_POLL_MS = 10_000
const RECEIPT_POLL_WINDOW_MS = 2 * 60_000

export function hasPendingReceipts(items: WalletTicketSummary[], nowMs = Date.now()): boolean {
  return items.some((item) => {
    if (String(item.receipt_status || '') !== 'pending') return false
    if (!item.purchased_at) return true
    const started = new Date(item.purchased_at).getTime()
    if (!Number.isFinite(started)) return true
    return (nowMs - started) <= RECEIPT_POLL_WINDOW_MS
  })
}

export default function WalletTickets() {
  const [items, setItems] = useState<WalletTicketSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openTicket, setOpenTicket] = useState<WalletTicketSummary | null>(null)
  const [refreshingReceipts, setRefreshingReceipts] = useState(false)
  const [receiptHealth, setReceiptHealth] = useState<{
    env_ok: boolean
    links_available: boolean
  } | null>(null)

  const refreshTickets = (opts?: { forceSpinner?: boolean }) => {
    if (opts?.forceSpinner) setLoading(true)
    setRefreshingReceipts(true)
    return getWalletTickets()
      .then((data) => {
        setItems(data.results || [])
      })
      .catch((e: any) => {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load tickets')
      })
      .finally(() => {
        setLoading(false)
        setRefreshingReceipts(false)
      })
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getWalletTickets()
      .then((data) => {
        if (cancelled) return
        setItems(data.results || [])
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(e?.response?.data?.detail || e?.message || 'Failed to load tickets')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!hasPendingReceipts(items)) return
    const timer = window.setInterval(() => {
      void refreshTickets()
    }, RECEIPT_POLL_MS)
    return () => window.clearInterval(timer)
  }, [items])

  useEffect(() => {
    let cancelled = false
    fetch('/api/health/receipts/')
      .then(async (res) => {
        if (!res.ok) return null
        const data = await res.json()
        return {
          env_ok: Boolean(data?.env_ok),
          links_available: Boolean(data?.links_available),
        }
      })
      .then((data) => {
        if (!cancelled && data) setReceiptHealth(data)
      })
      .catch(() => {
        // best-effort health signal only
      })
    return () => {
      cancelled = true
    }
  }, [])

  const hasItems = useMemo(() => items.length > 0, [items.length])

  if (loading) {
    return <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">Loading tickets...</div>
  }
  if (error) {
    return <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">{error}</div>
  }
  if (!hasItems) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-white/80">
        No tickets yet. Buy tickets on event pages.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div data-testid="wallet-ticket-card" key={`${item.post_id}`} className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Link to={`/p/${item.post_id}`} className="text-base font-semibold text-white hover:text-sky-300">
                {item.event_title}
              </Link>
              <div className="mt-1 text-sm text-white/70">{formatDateTime(item.starts_at)}</div>
              <div className="text-sm text-white/60">
                {[item.city_label, item.venue_name].filter(Boolean).join(' · ') || 'Location TBD'}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span data-testid="wallet-ticket-status-badge" className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClasses(item.status)}`}>
                {item.status === 'active' ? 'Active' : item.status === 'past' ? 'Past' : item.status === 'redeemed' ? 'Redeemed' : 'Cancelled'}
              </span>
              {item.receipt_status ? (
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${item.receipt_status === 'anchored' ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100' : 'border-amber-400/40 bg-amber-500/15 text-amber-100'}`}>
                  {receiptLabel(item.receipt_status)}
                </span>
              ) : null}
              {(item.receipt_hash && item.receipt_status !== 'anchored' && !receiptHealth?.env_ok) ? (
                <span className="mt-1 inline-flex items-center rounded-full border border-rose-400/40 bg-rose-500/10 px-2 py-0.5 text-[11px] font-semibold text-rose-200">
                  Receipt system warning
                </span>
              ) : null}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-sm text-white/85">
              Tickets: <span className="font-semibold text-white">{item.qty}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="wallet-view-ticket-button"
                onClick={() => setOpenTicket(item)}
                className="rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-white/90 hover:bg-white/10"
              >
                View ticket
              </button>
              <Link
                to={`/p/${item.post_id}`}
                className="rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-white/90 hover:bg-white/10"
              >
                View event
              </Link>
            </div>
          </div>
        </div>
      ))}
      <TicketQrModal
        open={Boolean(openTicket)}
        onClose={() => setOpenTicket(null)}
        title={openTicket?.event_title || 'Ticket'}
        qrImageUrl={openTicket?.ticket_serials?.[0] ? `/api/tickets/${openTicket.ticket_serials[0]}/qr.png` : null}
        qrPayload={openTicket?.ticket_qr_payloads?.[0] || null}
        receiptHash={openTicket?.receipt_hash || null}
        receiptStatus={openTicket?.receipt_status || null}
        pendingMessage={(() => {
          if (!openTicket || openTicket.receipt_status !== 'pending') return null
          const purchased = openTicket.purchased_at ? new Date(openTicket.purchased_at) : null
          const isOld = purchased ? (Date.now() - purchased.getTime()) > (15 * 60 * 1000) : false
          if (isOld || receiptHealth?.env_ok === false) {
            return 'Receipt verification is pending. If this persists, anchoring service may be offline.'
          }
          return null
        })()}
        linksAvailable={receiptHealth?.links_available !== false}
        onRefreshReceipt={() => { void refreshTickets() }}
        refreshingReceipt={refreshingReceipts}
      />
    </div>
  )
}
